import type { ReactNode } from "react"

import type { TokenStore } from "@/domains/tokens/token-store"
import { Lozenge } from "@/domains/common/components/lozenge"
import { AprBadge } from "@/domains/thick-v1/components/apr-badge"
import { BodyCaption } from "@/domains/design/components/body-caption"
import { BodySM } from "@/domains/design/components/body-sm"
import { BodyXl } from "@/domains/design/components/body-xl"
import { BodyXs } from "@/domains/design/components/body-xs"
import { WarningIcon } from "@/domains/icons/huge-icons/monotone/warning"
import { cn, dollarsPrettified } from "@/domains/library"
import { DisplayTokens } from "@/domains/tokens/components/display-tokens"

import type { PriceStrategy, PriceStrategyConfig } from "@/domains/thick-v1/stores/price-strategies"

import { DepositTokenInputView } from "./deposit-token-input-view"
import { PriceRangeInputView } from "./price-range-input-view"
import { PriceStrategySelectorView } from "./price-strategy-selector-view"

/**
 * Props for {@link CreatePositionCardView}.
 *
 * The view is deliberately "dumb": every piece of state and every callback is
 * passed in as a plain prop. The `CreatePositionCardComposite` next to this
 * file is the only production caller and is responsible for translating
 * `CreatePositionStore` observables into these props.
 *
 * Two narrow exceptions to "props only, no stores":
 *   - `token0` / `token1` are `TokenStore` instances. Other views in this
 *     directory (e.g. `DepositTokenInputView`) already accept a `TokenStore`
 *     because they rely on a handful of observables (`symbol`, `balance`,
 *     `displayUnits`, `bestPrice`). Plumbing every field separately would
 *     multiply props without improving testability.
 *   - `rangeBrush` is a `ReactNode` slot. The brush is itself a composite
 *     (MobX-reactive + observer-wrapped), so the outer composite mounts it
 *     and hands it in here as an opaque child rather than the view mounting
 *     the composite.
 */
export interface CreatePositionCardViewProps {
  // Header
  token0: TokenStore
  token1: TokenStore
  displayFee: string

  // Current price
  formattedPrice: string
  token0UsdPrice: string | undefined

  // Pool context strip
  tvlUsd: number
  volumeUsd24h: number
  feesUsd24h: number
  /**
   * Range-aware projected APR for the user's current range + deposit. This
   * replaces the old pool-wide `displayApr` on the create card so the badge
   * reflects what the specific position would earn rather than the pool
   * average. The view still renders `formattedPct` verbatim — math and
   * formatting live in `CreatePositionStore.estimatedApr`.
   */
  estimatedApr: {
    formattedPct: string
    formattedDailyUsd: string
    capitalEfficiency: number
    inRange: boolean
  }

  // Range preview
  isProposedRangeInRange: boolean
  liquidityCoveragePct: number

  // Price strategies
  priceStrategyConfigs: Array<PriceStrategyConfig>
  selectedStrategy: PriceStrategy | undefined
  isPriceStrategyDisabled: boolean
  onSelectStrategy: (strategy: PriceStrategy) => void
  rangeSpreadPctInput: string
  isRangeSpreadDisabled: boolean
  onSetRangeSpreadPctInput: (value: string) => void

  // Historical price chart (opaque composite slot). Rendered directly above
  // the tick-based liquidity brush so an LP can see whether the price has
  // actually been in the range they're about to pick.
  priceHistory?: ReactNode

  // Liquidity distribution range brush (opaque composite slot)
  rangeBrush?: ReactNode

  // Min/max price inputs. Empty-string min/max happen when the strategy has
  // not yet computed a range (e.g. before the pool loads); the nested
  // `PriceRangeInputView` already handles that as a no-op.
  minPrice: string
  maxPrice: string
  minPriceDifference: number | undefined
  maxPriceDifference: number | undefined
  minTickAtLimit: boolean
  maxTickAtLimit: boolean
  onIncrementMinPrice: () => void
  onDecrementMinPrice: () => void
  onIncrementMaxPrice: () => void
  onDecrementMaxPrice: () => void

  // Deposits
  amount0: string
  amount1: string
  token0BalanceLabel: string
  token1BalanceLabel: string
  onFillMaxAmount0: () => void
  onFillMaxAmount1: () => void
  onSetAmount0: (value: string) => void
  onSetAmount1: (value: string) => void
  onFocusToken0: () => void
  onFocusToken1: () => void
  totalDepositUsdValue: number
  isWalletConnected: boolean
  isToken0DepositDisabled: boolean
  isToken1DepositDisabled: boolean

  // CTA
  canCreatePosition: boolean
  ctaLabel: string
  onCreatePosition: () => void

  /**
   * Optional in-card stepper rendered while the create-position interaction
   * is running (or while we're surfacing a failure from a prior run). When
   * present, the CTA is hidden — the stepper is the only control the user
   * should interact with until the flow finishes. Kept as a `ReactNode`
   * slot so the view stays ignorant of the store shape.
   */
  processView?: ReactNode

  // Validation
  isRangeInvalid: boolean
}

/**
 * Pure Create Position Card view. Contains zero MobX wiring and is safe to
 * render from Storybook with static fixtures.
 */
export function CreatePositionCardView({
  token0,
  token1,
  displayFee,
  formattedPrice,
  token0UsdPrice,
  tvlUsd,
  volumeUsd24h,
  feesUsd24h,
  estimatedApr,
  isProposedRangeInRange,
  liquidityCoveragePct,
  priceStrategyConfigs,
  selectedStrategy,
  isPriceStrategyDisabled,
  onSelectStrategy,
  rangeSpreadPctInput,
  isRangeSpreadDisabled,
  onSetRangeSpreadPctInput,
  priceHistory,
  rangeBrush,
  minPrice,
  maxPrice,
  minPriceDifference,
  maxPriceDifference,
  minTickAtLimit,
  maxTickAtLimit,
  onIncrementMinPrice,
  onDecrementMinPrice,
  onIncrementMaxPrice,
  onDecrementMaxPrice,
  amount0,
  amount1,
  token0BalanceLabel,
  token1BalanceLabel,
  onFillMaxAmount0,
  onFillMaxAmount1,
  onSetAmount0,
  onSetAmount1,
  onFocusToken0,
  onFocusToken1,
  totalDepositUsdValue,
  isWalletConnected,
  isToken0DepositDisabled,
  isToken1DepositDisabled,
  canCreatePosition,
  ctaLabel,
  onCreatePosition,
  processView,
  isRangeInvalid,
}: CreatePositionCardViewProps) {
  return (
    <div className="w-full">
      <div className="group/container relative w-full max-w-4xl overflow-hidden rounded-3xl border border-p400-light bg-p300">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-p100/5 to-transparent opacity-0 transition-opacity group-hover/container:opacity-100"></div>

        {/* Header */}
        <div className="relative z-10 flex items-center justify-between p-6 pb-4">
          <div className="flex items-center gap-2">
            <DisplayTokens tokens={[token0, token1]} tokenSize={28} />
            <div className="flex flex-col">
              <div className="flex items-center gap-2">
                <BodyXl As="h2" className="font-black tracking-tight text-t100">
                  {token0.symbol} / {token1.symbol}
                </BodyXl>
                <Lozenge size="xxs" color="p400">
                  v3
                </Lozenge>
                <Lozenge size="xxs" color="p400">
                  {displayFee}
                </Lozenge>
              </div>
            </div>
          </div>
        </div>

        {/* Current Price */}
        <div className="relative z-10 px-6 pb-4">
          <BodySM As="p" className="mb-2 text-t200">
            Current price
          </BodySM>
          <div className="flex items-center gap-2">
            <BodyXl As="p" className="font-black tracking-tight text-t100">
              {formattedPrice}
            </BodyXl>
            {token0UsdPrice && (
              <BodySM As="span" className="text-t200">
                ({token0.symbol} = {token0UsdPrice})
              </BodySM>
            )}
          </div>
        </div>

        {/* Pool context strip */}
        <div className="relative z-10 px-6 pb-4">
          <div className="grid grid-cols-2 gap-3 rounded-2xl border border-p400-light bg-p400 p-4 sm:grid-cols-4">
            <PoolContextStat label="TVL" value={dollarsPrettified(tvlUsd)} />
            <PoolContextStat label="24h Volume" value={dollarsPrettified(volumeUsd24h)} />
            <PoolContextStat label="24h Fees" value={dollarsPrettified(feesUsd24h)} />
            <PoolContextStat
              label="Estimated APR"
              value={
                <AprBadge
                  value={estimatedApr.formattedPct}
                  variant="estimated"
                  outOfRange={!estimatedApr.inRange}
                />
              }
              hint={
                estimatedApr.inRange
                  ? formatEstimatedAprHint(
                      estimatedApr.formattedDailyUsd,
                      estimatedApr.capitalEfficiency,
                    )
                  : "Position will not earn until price re-enters your range"
              }
            />
          </div>
        </div>

        {/* Range preview */}
        <div className="relative z-10 flex flex-wrap items-center gap-2 px-6 pb-4">
          <span
            className={cn(
              "rounded-full px-2.5 py-1 text-xs font-medium",
              isProposedRangeInRange
                ? "bg-primary-400/15 text-primary-400"
                : "bg-t300/15 text-t300",
            )}
          >
            {isProposedRangeInRange ? "In range" : "Out of range"}
          </span>
          <span className="text-xs text-t300">
            Liquidity coverage ≈ {liquidityCoveragePct.toFixed(2)}% of full range
          </span>
        </div>

        {/* Price strategies */}
        <div className="relative z-10 px-6 pb-6">
          <div className="flex flex-col gap-3">
            <PriceStrategySelectorView
              strategies={priceStrategyConfigs}
              selectedStrategy={selectedStrategy}
              isDisabled={isPriceStrategyDisabled}
              onSelect={onSelectStrategy}
            />
            <RangeSpreadInput
              value={rangeSpreadPctInput}
              isDisabled={isRangeSpreadDisabled}
              onChange={onSetRangeSpreadPctInput}
            />
          </div>
        </div>

        {/* Historical price chart, rendered just above the liquidity brush
            so the two views read top-to-bottom as "has price been here?" →
            "where does liquidity sit?". Optional slot: callers without an
            OHLC store (e.g. Storybook isolation) can omit it cleanly. */}
        {priceHistory ? <div className="relative z-10 px-6 pb-4">{priceHistory}</div> : null}

        {/* Liquidity distribution range brush */}
        {rangeBrush ? <div className="relative z-10 px-6 pb-4">{rangeBrush}</div> : null}

        {/* Min/Max price inputs */}
        <div className="relative z-10 px-6 pb-6">
          <div className="flex gap-4">
            <PriceRangeInputView
              title="Min price"
              price={minPrice}
              priceDifference={minPriceDifference}
              onIncrement={onIncrementMinPrice}
              onDecrement={onDecrementMinPrice}
              isIncrementDisabled={false}
              isDecrementDisabled={minTickAtLimit}
            />
            <PriceRangeInputView
              title="Max price"
              price={maxPrice}
              priceDifference={maxPriceDifference}
              onIncrement={onIncrementMaxPrice}
              onDecrement={onDecrementMaxPrice}
              isIncrementDisabled={maxTickAtLimit}
              isDecrementDisabled={false}
            />
          </div>
        </div>

        {/* Deposit tokens */}
        <div className="relative z-10 px-6 pb-6">
          <div className="mb-4 flex items-end justify-between">
            <div>
              <BodyXl As="h3" className="font-black tracking-tight text-t100">
                Deposit tokens
              </BodyXl>
              <BodySM As="p" className="text-t200">
                Specify the token amounts for your liquidity contribution.
              </BodySM>
            </div>
            {totalDepositUsdValue > 0 && (
              <div className="text-right">
                <BodySM As="p" className="text-t300">
                  Total value
                </BodySM>
                <BodyXl As="p" className="font-semibold text-t100">
                  {dollarsPrettified(totalDepositUsdValue)}
                </BodyXl>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <DepositTokenInputView
              label="Deposit"
              token={token0}
              amount={amount0}
              balanceLabel={token0BalanceLabel}
              onBalanceClick={onFillMaxAmount0}
              onFocus={onFocusToken0}
              onChangeAmount={onSetAmount0}
              isDisabled={!isWalletConnected || isToken0DepositDisabled}
            />
            <DepositTokenInputView
              label="Deposit"
              token={token1}
              amount={amount1}
              balanceLabel={token1BalanceLabel}
              onBalanceClick={onFillMaxAmount1}
              onFocus={onFocusToken1}
              onChangeAmount={onSetAmount1}
              isDisabled={!isWalletConnected || isToken1DepositDisabled}
            />
          </div>
        </div>

        {/* CTA / Process view. The stepper replaces the CTA while the
            interaction is running so the user doesn't see a "live" button
            promising another click when the flow is already underway. */}
        <div className="relative z-10 px-6 pb-6">
          {processView ? (
            processView
          ) : (
            <button
              type="button"
              disabled={!isWalletConnected || !canCreatePosition}
              onClick={onCreatePosition}
              className={cn(
                "w-full rounded-2xl px-6 py-5 text-center text-xl font-black tracking-tight transition-all",
                !isWalletConnected || !canCreatePosition
                  ? "cursor-not-allowed bg-p400 text-t300"
                  : "bg-p100 text-t100 hover:bg-p100-light active:bg-p100-shade",
              )}
            >
              {ctaLabel}
            </button>
          )}
        </div>

        {/* Invalid Range Warning */}
        {isRangeInvalid && (
          <div className="relative z-10 flex items-center gap-2 px-6 pb-6">
            <WarningIcon className="h-4 w-4 text-error-text" />
            <BodySM As="p" className="text-error-text">
              Invalid range
            </BodySM>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Join the optional daily-USD projection with the capital-efficiency
 * phrase, dropping the daily-USD fragment (and the separator) when the
 * store reports no deposit — the size-independent baseline APR path
 * legitimately has no per-day number and rendering "$0.00/day" there
 * reads as if the range itself was worthless.
 */
function formatEstimatedAprHint(
  formattedDailyUsd: string,
  capitalEfficiency: number,
): string {
  const capitalEfficiencyText = formatCapitalEfficiency(capitalEfficiency)
  if (!formattedDailyUsd) return capitalEfficiencyText
  return `${formattedDailyUsd} • ${capitalEfficiencyText}`
}

/**
 * Render `3.4x capital efficiency vs full range` from the raw multiplier
 * exposed by `concentratedApr`. Centralised here so the capital-efficiency
 * phrasing stays consistent if we surface it elsewhere.
 */
function formatCapitalEfficiency(multiplier: number): string {
  if (!Number.isFinite(multiplier) || multiplier <= 1.05) return "1× capital efficiency"
  if (multiplier >= 1_000) return `${Math.round(multiplier).toLocaleString()}× capital efficiency`
  if (multiplier >= 100) return `${Math.round(multiplier)}× capital efficiency`
  return `${multiplier.toFixed(1)}× capital efficiency`
}

function RangeSpreadInput({
  value,
  isDisabled,
  onChange,
}: {
  value: string
  isDisabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <div
      className={cn(
        "flex w-full flex-wrap items-center justify-between gap-3 rounded-lg border px-4 py-3 text-left transition-all",
        isDisabled
          ? "cursor-not-allowed border-p400-light bg-p400/50 opacity-60"
          : "border-p400-light bg-p400 hover:border-p100 hover:bg-p300-light",
      )}
    >
      <div className="flex flex-col gap-2">
        <BodyXs As="span" className="text-t200">
          Set by spread
        </BodyXs>
        <BodyCaption As="span" className="text-t200">
          Set min/max prices by entering a percent either side of current price.
        </BodyCaption>
      </div>
      <label className="flex items-center gap-2">
        <span className="text-sm font-semibold text-t100">±</span>
        <input
          type="text"
          inputMode="decimal"
          value={value}
          disabled={isDisabled}
          onChange={(event) => onChange(event.target.value)}
          placeholder="5"
          className={cn(
            "h-10 w-20 rounded-lg border border-p300-light bg-p300 px-3 text-center text-base font-semibold tabular-nums text-t100 outline-none transition-colors placeholder:text-t300 focus:border-p100 focus:bg-p300-light disabled:cursor-not-allowed disabled:opacity-50",
          )}
          aria-label="Range spread percent"
          data-testid="create-position-spread-input"
        />
        <span className="text-sm font-semibold text-t100">%</span>
      </label>
    </div>
  )
}

function PoolContextStat({
  label,
  value,
  hint,
}: {
  label: string
  value: ReactNode
  hint?: string
}) {
  const content =
    typeof value === "string" || typeof value === "number" ? (
      <span className="text-t100 font-semibold">{value}</span>
    ) : (
      <div className="mt-0.5">{value}</div>
    )
  return (
    <div className="flex flex-col">
      <BodySM As="p" className="text-[11px] uppercase tracking-wide text-t300">
        {label}
      </BodySM>
      {content}
      {hint && <span className="mt-0.5 text-[10px] leading-snug text-t300">{hint}</span>}
    </div>
  )
}
